import { supabase } from "@/lib/supabase/client";
import { getMyProfileId } from "@/features/identity/api/profileApi";

const LINK_COLUMNS = "id,profile_id,platform,url,display_order,is_visible,created_at";

const check = ({ error }) => {
  if (error) throw error;
};

export async function listMyLinks(profileId) {
  const pid = profileId ?? (await getMyProfileId());
  const { data, error } = await supabase
    .from("profile_links")
    .select(LINK_COLUMNS)
    .eq("profile_id", pid)
    .order("display_order", { ascending: true })
    .order("created_at", { ascending: true });
  if (error) throw error;
  return data;
}

const toRecord = (row, index) => ({
  platform: row.platform,
  url: row.url.trim(),
  is_visible: row.is_visible,
  display_order: index,
});

const changed = (orig, row, index) =>
  !orig ||
  orig.platform !== row.platform ||
  orig.url !== row.url.trim() ||
  orig.is_visible !== row.is_visible ||
  orig.display_order !== index;

// Diff-based save: delete removed, update changed, insert new, rewrite display_order 0..n.
export async function saveLinks(rows, original) {
  const pid = await getMyProfileId();
  const keep = new Set(rows.filter((r) => r.id).map((r) => r.id));
  const removed = original.filter((o) => !keep.has(o.id)).map((o) => o.id);
  if (removed.length) check(await supabase.from("profile_links").delete().in("id", removed).eq("profile_id", pid));

  const byId = new Map(original.map((o) => [o.id, o]));
  const updates = rows
    .map((row, i) => ({ row, i }))
    .filter(({ row, i }) => row.id && changed(byId.get(row.id), row, i));
  const results = await Promise.all(
    updates.map(({ row, i }) =>
      supabase.from("profile_links").update(toRecord(row, i)).eq("id", row.id).eq("profile_id", pid),
    ),
  );
  results.forEach(check);

  const inserts = rows
    .map((row, i) => ({ row, i }))
    .filter(({ row }) => !row.id)
    .map(({ row, i }) => ({ profile_id: pid, ...toRecord(row, i) }));
  if (inserts.length) check(await supabase.from("profile_links").insert(inserts));

  return listMyLinks(pid);
}
